import { message } from 'antd';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { NamePath } from 'antd/lib/form/interface';
import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { ifProp, prop } from 'styled-tools';
import {
  IntegrationProvider,
  JiraConfig,
  JiraIssueType,
  MappingMechanism,
} from 'api/integrations';
import { Checkbox } from 'components/atoms/checkbox';
import { Form } from 'components/atoms/form';
import { PageLoading } from 'components/atoms/loading';
import { getJiraFormValues } from 'components/molecules/integrations-commons';
import { useProjectTemplateTask } from 'data/templates/projects';
import { useTranslation } from 'hooks/use-translation';
import {
  useQueryGetJiraFields,
  useQueryJiraEpic,
  useQueryJiraIssueTypes,
} from 'queries/jira-integration';
import { queueMacrotask } from 'utils/macrotask';
import {
  useTemplateActiveTaskLinkedResource,
  useTemplateTaskCommonActions,
} from '../../../../commons';
import { JiraTemplatesFormOld } from './jira-templates-form';

type JiraPane = {
  jiraConfig: JiraConfig;
  taskId: number;
};

const Card = styled.div`
  height: calc(100vh - 240px);
  padding: 32px;
  background: #fff;
  overflow: scroll;
  min-height: 50vh;
  width: 688px;
  margin: auto;
`;

const FormContainer = styled.div<{ disable?: boolean }>`
  ${ifProp(
    prop('disable', true),
    css`
      opacity: 0.5;
      cursor: not-allowed;
      pointer-events: none;
    `,
  )};
`;

const CheckboxContainer = styled.div`
  margin-bottom: 32px;
`;

export const JiraPaneOld: FC<JiraPane> = ({ jiraConfig, taskId }) => {
  const enableJiraText = useTranslation('templates.jira.enable_jira');
  const loadingText = useTranslation('templates.jira.enable_jira.loading');
  const linkedResources = useTemplateActiveTaskLinkedResource([
    'task_id',
    taskId,
  ]);
  const [form] = Form.useForm();
  const taskData = useProjectTemplateTask(taskId);
  const { onJiraFormChange } = useTemplateTaskCommonActions(taskData);

  const [enableMapping, setEnableMapping] = useState(false);

  const {
    integrationId,
    settingsMeta: {
      fields,
      fixedProjectId,
      fixedEpicId: jiraFixedEpicId,
      defaultIssueTypeId = '',
      epicMappingMechanism,
    },
  } = jiraConfig;

  const savedJiraConfig = useMemo(
    () =>
      linkedResources?.find(
        ({ externalIntegrationType }) =>
          externalIntegrationType === IntegrationProvider.Jira,
      ),
    [linkedResources],
  );

  const { data: epicsList, isLoading: loadingEpics } = useQueryJiraEpic(
    jiraConfig.integrationId,
    jiraConfig.settingsMeta.fixedProjectId,
    {
      refetchOnWindowFocus: false,
      onSuccess: (epics) => {
        const selectedEpic = epics.find(({ id }) => id === jiraFixedEpicId);

        if (selectedEpic) {
          form.setFieldsValue({
            epicId: {
              value: selectedEpic.id,
              valueStr: selectedEpic.id,
              label: selectedEpic.name,
            },
          });
        }

        return true;
      },
    },
  );

  const { data: issueTypes, isLoading: loadingIssueTypes } =
    useQueryJiraIssueTypes(
      jiraConfig.integrationId,
      jiraConfig.settingsMeta.fixedProjectId,
      {
        refetchOnWindowFocus: false,
        onSuccess: (res: JiraIssueType[]) => {
          const selectedIssueType = (res ?? []).find(
            ({ id }) => id === jiraConfig.settingsMeta.defaultIssueTypeId,
          );

          if (
            selectedIssueType != null &&
            savedJiraConfig?.externalResourceInputs?.issuetype == null
          ) {
            form.setFieldsValue({
              issuetype: {
                value: selectedIssueType.id,
                valueStr: selectedIssueType.id,
                label: selectedIssueType.name,
              },
            });
          }
        },
      },
    );

  const { data: fieldsList, isLoading: loadingFields } = useQueryGetJiraFields(
    integrationId,
    fixedProjectId,
    defaultIssueTypeId,
    {
      refetchOnWindowFocus: false,
    },
  );

  const onConnect = (changedData?: NamePath[]) => {
    const payload = form.getFieldsValue(true);

    queueMacrotask(() => {
      form.validateFields(changedData).then(() => {
        onJiraFormChange({
          taskId: taskData.taskId,
          data: {
            ...payload,
            issuetype: payload?.issuetype?.value,
            epicId: payload?.epicId?.value,
          },
        });
      });
    });
  };

  const onDisconnect = () => {
    onJiraFormChange({
      taskId: taskData.taskId,
      data: undefined,
    });
  };

  const onStatusToggle = (e: CheckboxChangeEvent) => {
    const {
      target: { checked },
    } = e;

    if (loadingFields || loadingEpics || loadingIssueTypes) {
      message.info(loadingText);

      return false;
    }

    if (checked) {
      onConnect();
    } else {
      onDisconnect();
    }

    setEnableMapping(checked);

    return true;
  };

  const savedEpicId = useMemo(
    () =>
      (epicsList ?? [])
        .filter(({ id }) => id === jiraFixedEpicId)
        ?.reduce(
          (acc, { id, name }) => ({
            value: id,
            valueStr: id,
            label: name,
          }),
          {},
        ),
    [epicsList, jiraFixedEpicId],
  );

  const savedIssueType = useMemo(
    () =>
      (issueTypes ?? [])
        .filter(
          ({ id }) => id === savedJiraConfig?.externalResourceInputs?.issuetype,
        )
        ?.reduce(
          (acc, { id, name }) => ({
            value: id,
            valueStr: id,
            label: name,
          }),
          {},
        ),
    [issueTypes, savedJiraConfig],
  );

  const formSetFieldsValueRef = useRef(form.setFieldsValue);

  useEffect(() => {
    formSetFieldsValueRef.current = form.setFieldsValue;
  }, [formSetFieldsValueRef, form]);

  useEffect(() => {
    const setEpicValue =
      epicMappingMechanism === MappingMechanism.PromptDuringIssuecreation ||
      epicMappingMechanism === MappingMechanism.Fixed;

    if (savedJiraConfig != null) {
      formSetFieldsValueRef.current?.({
        ...savedJiraConfig?.externalResourceInputs,
        epicId: setEpicValue ? savedEpicId : undefined,
        issuetype: savedIssueType,
      });
      setEnableMapping(true);
    } else {
      formSetFieldsValueRef.current?.({
        ...getJiraFormValues(fields),
        summary: taskData?.taskName,
        description: taskData?.taskDescription ?? '',
      });
    }
  }, [
    formSetFieldsValueRef,
    setEnableMapping,
    savedEpicId,
    fields,
    taskData,
    epicMappingMechanism,
    savedIssueType,
    savedJiraConfig,
  ]);

  return (
    <Card>
      <CheckboxContainer>
        <Checkbox
          checked={enableMapping}
          onChange={onStatusToggle}
          disabled={loadingFields || loadingEpics || loadingIssueTypes}
        >
          {enableJiraText}
        </Checkbox>
      </CheckboxContainer>

      <FormContainer disable={!enableMapping}>
        {loadingFields || loadingEpics || loadingIssueTypes ? (
          <PageLoading />
        ) : (
          <JiraTemplatesFormOld
            jiraConfig={jiraConfig}
            taskData={taskData}
            form={form}
            fieldsList={fieldsList}
            issueTypes={issueTypes}
            epicsList={epicsList}
            disabled={!enableMapping}
            onChange={onConnect}
          />
        )}
      </FormContainer>
    </Card>
  );
};
